import React, { useState, useEffect } from 'react';
import { Member } from '../../types';
import apiService from '../../services/api';

interface MemberFormProps {
  member?: Member | null;
  onSubmit: () => void;
  onCancel: () => void;
}

const MemberForm: React.FC<MemberFormProps> = ({ member, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    title: '博士生' as Member['title'],
    email: '',
    phone: '',
    avatar: '',
    bio: '',
    researchInterests: [''],
    education: [{ degree: '', institution: '', year: new Date().getFullYear() }],
    experience: [{ position: '', institution: '', startYear: new Date().getFullYear(), endYear: undefined, current: false }] as any,
    awards: [{ name: '', year: new Date().getFullYear(), description: '' }],
    isActive: true,
    order: 0,
    memberType: '组内成员（在读学生）', // 新增字段，默认值
    facultyRole: '组内教师' as '实验室负责人' | '组内教师',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (member) {
      setFormData({
        name: member.name,
        title: member.title,
        email: member.email,
        phone: member.phone || '',
        avatar: member.avatar || '',
        bio: member.bio || '',
        researchInterests: member.researchInterests || [''],
        education: member.education || [{ degree: '', institution: '', year: new Date().getFullYear() }],
        experience: member.experience?.map((exp: any, index: any) => ({
          ...exp,
          endYear: typeof exp.endYear === 'number' ? exp.endYear : undefined
        })) as any || [{ position: '', institution: '', startYear: new Date().getFullYear(), endYear: undefined, current: false }] as any,
        awards: member.awards || [{ name: '', year: new Date().getFullYear(), description: '' }],
        isActive: member.isActive,
        order: member.order,
        memberType: member.memberType || '组内成员（在读学生）',
        facultyRole: member.facultyRole || '组内教师',
      });
    }
  }, [member]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (member) {
        await apiService.put(`/members/${member._id}`, formData);
      } else {
        await apiService.post('/members', formData);
      }
      onSubmit();
    } catch (error: any) {
      console.error('保存失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const addArrayItem = (field: keyof typeof formData, item: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev[field] as any[]), item]
    }));
  };

  const removeArrayItem = (field: keyof typeof formData, index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] as any[]).filter((_, i) => i !== index)
    }));
  };

  const updateArrayItem = (field: keyof typeof formData, index: number, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] as any[]).map((item, i) => i === index ? value : item)
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {member ? '编辑成员' : '添加成员'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 基本信息 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">姓名</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">职位</label>
                <select
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value as Member['title'] }))}
                  className="input-field"
                >
                  <option value="研究员">研究员</option>
                  <option value="副研究员">副研究员</option>
                  <option value="教授">教授</option>
                  <option value="副教授">副教授</option>
                  <option value="讲师">讲师</option>
                  <option value="博士后">博士后</option>
                  <option value="博士生">博士生</option>
                  <option value="硕士生">硕士生</option>
                  <option value="本科生">本科生</option>
                  <option value="访问学者">访问学者</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">邮箱</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">电话</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="input-field"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">头像URL</label>
              <input
                type="url"
                value={formData.avatar}
                onChange={(e) => setFormData(prev => ({ ...prev, avatar: e.target.value }))}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">个人简介</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                rows={3}
                className="input-field"
              />
            </div>

            {/* 研究方向 */}
            <div>
              <label className="block text-sm font-medium text-gray-700">研究方向</label>
              {formData.researchInterests.map((interest, index) => (
                <div key={index} className="flex space-x-2 mb-2">
                  <input
                    type="text"
                    value={interest}
                    onChange={(e) => updateArrayItem('researchInterests', index, e.target.value)}
                    className="input-field flex-1"
                    placeholder="研究方向"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem('researchInterests', index)}
                    className="px-3 py-2 text-red-600 hover:text-red-800"
                  >
                    删除
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('researchInterests', '')}
                className="text-primary-600 hover:text-primary-800"
              >
                + 添加研究方向
              </button>
            </div>

            {/* 教育背景 */}
            <div>
              <label className="block text-sm font-medium text-gray-700">教育背景</label>
              {formData.education.map((edu, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <input
                    type="text"
                    value={edu.degree}
                    onChange={(e) => updateArrayItem('education', index, { ...edu, degree: e.target.value })}
                    className="input-field"
                    placeholder="学位"
                  />
                  <input
                    type="text"
                    value={edu.institution}
                    onChange={(e) => updateArrayItem('education', index, { ...edu, institution: e.target.value })}
                    className="input-field"
                    placeholder="学校"
                  />
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      value={edu.year}
                      onChange={(e) => updateArrayItem('education', index, { ...edu, year: parseInt(e.target.value) })}
                      className="input-field"
                      placeholder="年份"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('education', index)}
                      className="px-3 py-2 text-red-600 hover:text-red-800"
                    >
                      删除
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('education', { degree: '', institution: '', year: new Date().getFullYear() })}
                className="text-primary-600 hover:text-primary-800"
              >
                + 添加教育背景
              </button>
            </div>

            {/* 工作经历 */}
            <div>
              <label className="block text-sm font-medium text-gray-700">工作经历</label>
              {formData.experience.map((exp: any, index: any) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                  <input
                    type="text"
                    value={exp.position}
                    onChange={(e) => updateArrayItem('experience', index, { ...exp, position: e.target.value })}
                    className="input-field"
                    placeholder="职位"
                  />
                  <input
                    type="text"
                    value={exp.institution}
                    onChange={(e) => updateArrayItem('experience', index, { ...exp, institution: e.target.value })}
                    className="input-field"
                    placeholder="机构"
                  />
                  <input
                    type="number"
                    value={exp.startYear}
                    onChange={(e) => updateArrayItem('experience', index, { ...exp, startYear: parseInt(e.target.value) })}
                    className="input-field"
                    placeholder="开始年份"
                  />
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      value={exp.endYear || ''}
                      onChange={(e) => updateArrayItem('experience', index, { ...exp, endYear: parseInt(e.target.value) })}
                      className="input-field"
                      placeholder="结束年份"
                      disabled={exp.current}
                    />
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={exp.current}
                        onChange={(e) => updateArrayItem('experience', index, { ...exp, current: e.target.checked })}
                        className="mr-2"
                      />
                      至今
                    </label>
                    <button
                      type="button"
                      onClick={() => removeArrayItem('experience', index)}
                      className="px-3 py-2 text-red-600 hover:text-red-800"
                    >
                      删除
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('experience', { position: '', institution: '', startYear: new Date().getFullYear(), endYear: undefined, current: false })}
                className="text-primary-600 hover:text-primary-800"
              >
                + 添加工作经历
              </button>
            </div>

            {/* 获奖情况 */}
            <div>
              <label className="block text-sm font-medium text-gray-700">获奖情况</label>
              {formData.awards.map((award, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <input
                    type="text"
                    value={award.name}
                    onChange={(e) => updateArrayItem('awards', index, { ...award, name: e.target.value })}
                    className="input-field"
                    placeholder="奖项名称"
                  />
                  <input
                    type="number"
                    value={award.year}
                    onChange={(e) => updateArrayItem('awards', index, { ...award, year: parseInt(e.target.value) })}
                    className="input-field"
                    placeholder="获奖年份"
                  />
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={award.description}
                      onChange={(e) => updateArrayItem('awards', index, { ...award, description: e.target.value })}
                      className="input-field"
                      placeholder="描述"
                    />
                    <button
                      type="button"
                      onClick={() => removeArrayItem('awards', index)}
                      className="px-3 py-2 text-red-600 hover:text-red-800"
                    >
                      删除
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('awards', { name: '', year: new Date().getFullYear(), description: '' })}
                className="text-primary-600 hover:text-primary-800"
              >
                + 添加获奖情况
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">注册类型</label>
                <select
                  value={formData.memberType}
                  onChange={e => setFormData(prev => ({ ...prev, memberType: e.target.value }))}
                  className="input-field"
                  required
                >
                  <option value="组内成员（在读学生）">组内成员（在读学生）</option>
                  <option value="组内成员（已毕业）">组内成员（已毕业）</option>
                  <option value="组内教师">组内教师</option>
                  <option value="组外成员">组外成员</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">教师角色</label>
                <select
                  value={formData.facultyRole}
                  onChange={e => setFormData(prev => ({ ...prev, facultyRole: e.target.value as '实验室负责人' | '组内教师' }))}
                  className="input-field"
                  disabled={formData.memberType !== '组内教师'}
                >
                  <option value="组内教师">组内教师</option>
                  <option value="实验室负责人">实验室负责人</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="mr-2"
              />
              <label className="text-sm font-medium text-gray-700">活跃状态</label>
            </div>

            {/* 按钮 */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="btn-secondary"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                {loading ? '保存中...' : (member ? '更新' : '创建')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MemberForm; 